import { FLEET_HAND_BACK_CHECKING, FLEET_RUN_ON_MINE_MINUTES, handedBackText } from './claude-fleet.hand-back'
import type { FleetHandBackCheck } from './claude-fleet.hand-back'
import { closeReportsTitle, FLEET_RUN_REPORTS_HELP } from './claude-fleet.run-reports'
import type { FleetRunReportsCheck } from './claude-fleet.run-reports'
import { recordingKeptText } from './claude-fleet.live'
import { runTouched } from './claude-fleet.runs'
import { isLaptopRun, isRunnerOwner } from './claude-fleet.runners'
import type { FleetRun, FleetRunner } from './claude-fleet.types'

/**
 * The ⋮ menu on a run's row in Running now and the Runs table. It offers only what applies to the run's
 * state and where it ran: an AgentCore run has a session to message, pause and resume; a laptop run has
 * none, and is stopped through its laptop's own command. Writes are for operators, and a laptop's owner may
 * stop a run on their own laptop.
 */

export type FleetRunMenuChoice =
  | 'view'
  | 'watch'
  | 'say'
  | 'pause'
  | 'resume'
  | 'stop'
  | 'hand-back'
  | 'run-on-mine'
  | 'handed-back'
  | 'rerun'
  | 'close-reports'
  | 'notify'
  | 'recording'
  | 'open-ticket'
  | 'open-pr'
  | 'copy-ticket-link'
  | 'copy-link'
  | 'copy-id'

export interface FleetRunMenuItem {
  key: FleetRunMenuChoice
  title: string
  disabled?: boolean
  /** Why it is disabled, or where it leads. */
  help?: string
  /** For the open choices: the link. */
  url?: string
}

export const FLEET_RUN_MENU_OPERATORS_ONLY = 'Only fleet operators can do this.'
export const FLEET_RUN_MENU_LAPTOP_STOP_ONLY = 'Only fleet operators and the runner\'s owner can stop this run.'
export const FLEET_RUN_MENU_LAPTOP_UNKNOWN = 'The machine running this is not listed as a live runner, so it cannot be told to stop.'
export const FLEET_RUN_STOP_LINK_ENDED = 'This run has already ended, so there is nothing to stop.'
export const FLEET_RUN_MENU_CLOSE_REPORTS_ONLY = 'Only fleet operators and the runner\'s owner can close its reports here.'

export interface FleetRunMenuOptions {
  /** This person is a fleet operator. */
  canAct: boolean
  /** This person may run the run's activity again; absent, only an operator may. */
  canRerun?: boolean
  /** This person owns the laptop the run is on. */
  isLaptopOwner: boolean
  /** The machine of the live laptop runner the run is on, when there is exactly one. */
  laptopMachine: string | null
  /** This person asked for a Slack DM when the run finishes. */
  isNotifying?: boolean
  /** The fleet's answer on whether this person may hand the run back; absent, the menu offers no hand-back. */
  handBack?: FleetHandBackCheck
  /** The Claude reports an ended run left pending on its PR, as the fleet answered; absent, the menu offers no Close. */
  pendingReports?: FleetRunReportsCheck
  /** The time the menu opens, for whether a recording is still kept. */
  now?: number
}

/**
 * What a link asking to stop a run does once the run loads: the Stop its menu would offer, or why there is none.
 * `ended` marks a run that has already ended, whose pending reports on its PR the link closes instead.
 */
export type FleetRunStopLink = { stop: true } | { stop: false; why: string; ended?: true }

/**
 * A pending Claude report on a PR links its run with `&stop=1`, so someone who no longer needs the run can end it.
 * The link offers exactly the Stop the run's menu would, under the same rules, and says why when there is none.
 */
export function runStopFromLink(run: FleetRun, options: Pick<FleetRunMenuOptions, 'canAct' | 'isLaptopOwner' | 'laptopMachine'>): FleetRunStopLink {
  const item = runMenuItems(run, options).find(menuItem => menuItem.key === 'stop')
  if (!item) return { stop: false, why: FLEET_RUN_STOP_LINK_ENDED, ended: true }
  return item.disabled ? { stop: false, why: item.help ?? FLEET_RUN_MENU_OPERATORS_ONLY } : { stop: true }
}

/** The one live runner of the run's owner, which is where its stop command goes; null when there is none or several. */
export function runLaptopMachine(run: Pick<FleetRun, 'ranOn' | 'runnerOwner'>, runners: FleetRunner[]): string | null {
  if (!isLaptopRun(run) || !run.runnerOwner) return null
  const owner = run.runnerOwner.toLowerCase()
  const live = runners.filter(runner => runner.live && runner.owner.toLowerCase() === owner)
  return live.length === 1 ? live[0]!.machine : null
}

/** Who may stop a laptop run and where its stop goes: the run's menu and a Stop this run link read the same answer. */
export function runStopRights(
  run: Pick<FleetRun, 'ranOn' | 'runnerOwner'>,
  runners: FleetRunner[],
  email: string | null | undefined
): Pick<FleetRunMenuOptions, 'isLaptopOwner' | 'laptopMachine'> {
  return {
    laptopMachine: runLaptopMachine(run, runners),
    isLaptopOwner: Boolean(run.runnerOwner) && isRunnerOwner(run.runnerOwner!, email)
  }
}

/** Hand back… and Run it on mine…, enabled or disabled as the fleet answered; one line once someone handed it back. */
export function handBackMenuItems(check: FleetHandBackCheck): FleetRunMenuItem[] {
  if (check.status === 'loading') {
    return [
      { key: 'hand-back', title: 'Hand back…', disabled: true, help: FLEET_HAND_BACK_CHECKING },
      { key: 'run-on-mine', title: 'Run it on mine…', disabled: true, help: FLEET_HAND_BACK_CHECKING }
    ]
  }
  if (check.status === 'failed') {
    return [
      { key: 'hand-back', title: 'Hand back…', disabled: true, help: check.why },
      { key: 'run-on-mine', title: 'Run it on mine…', disabled: true, help: check.why }
    ]
  }
  const { handBack, runOnMine, handedBack } = check.eligibility
  if (handedBack) return [{ key: 'handed-back', title: handedBackText(handedBack), disabled: true }]
  return [
    handBack.allowed ? { key: 'hand-back', title: 'Hand back…' } : { key: 'hand-back', title: 'Hand back…', disabled: true, help: handBack.why },
    runOnMine.allowed
      ? { key: 'run-on-mine', title: 'Run it on mine…', help: `Your runner ${runOnMine.machine} gets it first for ${FLEET_RUN_ON_MINE_MINUTES} minutes.` }
      : { key: 'run-on-mine', title: 'Run it on mine…', disabled: true, help: runOnMine.why }
  ]
}

function write(key: FleetRunMenuChoice, title: string, allowed: boolean, reason: string): FleetRunMenuItem {
  return allowed ? { key, title } : { key, title, disabled: true, help: reason }
}

/** Close its pending Claude review, for an ended run whose PR still shows a report it left pending. */
function closeReportsItems(check: FleetRunReportsCheck | undefined, allowed: boolean): FleetRunMenuItem[] {
  const title = check?.status === 'ready' ? closeReportsTitle(check.reports) : null
  if (!title) return []
  return [allowed ? { key: 'close-reports', title, help: FLEET_RUN_REPORTS_HELP } : write('close-reports', title, false, FLEET_RUN_MENU_CLOSE_REPORTS_ONLY)]
}

export function runMenuItems(run: FleetRun, options: FleetRunMenuOptions): FleetRunMenuItem[] {
  const { canAct, canRerun = canAct, isLaptopOwner, laptopMachine, isNotifying = false, handBack, pendingReports, now = Date.now() } = options
  const laptop = isLaptopRun(run)
  const items: FleetRunMenuItem[] = [{ key: 'view', title: 'View details' }]

  if (run.state === 'running' || run.state === 'paused') {
    if (run.browser && !laptop) items.push({ key: 'watch', title: 'Watch live' })
    if (laptop) {
      if (!laptopMachine) items.push({ key: 'stop', title: 'Stop…', disabled: true, help: FLEET_RUN_MENU_LAPTOP_UNKNOWN })
      else items.push(write('stop', 'Stop…', canAct || isLaptopOwner, FLEET_RUN_MENU_LAPTOP_STOP_ONLY))
    } else if (run.state === 'running') {
      items.push(write('say', 'Say…', canAct, FLEET_RUN_MENU_OPERATORS_ONLY))
      items.push(write('pause', 'Pause', canAct, FLEET_RUN_MENU_OPERATORS_ONLY))
      items.push(write('stop', 'Stop…', canAct, FLEET_RUN_MENU_OPERATORS_ONLY))
    } else {
      items.push(write('resume', 'Resume', canAct, FLEET_RUN_MENU_OPERATORS_ONLY))
      items.push(write('stop', 'Stop…', canAct, FLEET_RUN_MENU_OPERATORS_ONLY))
    }
    if (handBack) items.push(...handBackMenuItems(handBack))
    items.push(
      isNotifying
        ? { key: 'notify', title: 'Stop telling me when it\'s done' }
        : { key: 'notify', title: 'Tell me when it\'s done', help: 'A Slack DM when this run finishes.' }
    )
  } else {
    items.push(write('rerun', 'Run again…', canRerun, FLEET_RUN_MENU_OPERATORS_ONLY))
    items.push(...closeReportsItems(pendingReports, canAct || isLaptopOwner))
    const kept = recordingKeptText(run.recording, now)
    if (run.recording && kept) items.push({ key: 'recording', title: 'Open the browser recording', url: run.recording.consoleUrl, help: kept })
  }

  const touched = runTouched(run)
  const ticket = touched.tickets[0]
  const pullRequest = touched.pullRequests[0]
  if (ticket) items.push({ key: 'open-ticket', title: `Open ${ticket.label}`, url: ticket.url, help: ticket.url })
  if (pullRequest) items.push({ key: 'open-pr', title: `Open ${pullRequest.label}`, url: pullRequest.url, help: pullRequest.url })
  if (ticket) items.push({ key: 'copy-ticket-link', title: `Copy link to ${ticket.label}`, url: ticket.url })
  items.push({ key: 'copy-link', title: 'Copy link to this run' })
  items.push({ key: 'copy-id', title: 'Copy run id' })
  return items
}
